/**
 * Stripe Migration Panel
 *
 * Admin component for migrating existing Stripe customers.
 * Supports 3 levels:
 * 1. API Sync - Automatic sync from Stripe
 * 2. CSV Import - Bulk import with client mapping
 * 3. Manual Association - One-by-one association
 */

import React, { useState, useRef, useEffect } from 'react';
import {
  RefreshCw,
  Upload,
  Link2,
  Loader2,
  Check,
  AlertCircle,
  CreditCard,
  Building2,
  ChevronDown,
  ChevronUp,
  Search,
  FileText,
  Download,
} from 'lucide-react';
import {
  useSyncStripeCustomers,
  useImportStripeCustomers,
  useAssociateStripeCustomer,
  useUnassociatedStripeCustomers,
} from '../hooks/useStripeMigration';
import { stripeConnectAPI } from '@/utils/api';
import type { StripeCSVImportRow, UnassociatedStripeCustomer, SuggestedClient } from '../types/mandate.types';
import { logger } from '@/utils/logger';

type Tab = 'sync' | 'import' | 'manual';

export function StripeMigrationPanel() {
  const [activeTab, setActiveTab] = useState<Tab>('sync');
  const [stripeConnected, setStripeConnected] = useState(false);
  const [loading, setLoading] = useState(true);

  // Fetch Stripe connection status
  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const data = await stripeConnectAPI.getStatus();
        if (data.success && data.data) {
          // Consider Stripe connected if account exists and is enabled
          setStripeConnected(data.data.exists && data.data.chargesEnabled);
        }
      } catch {
        // If error, assume not connected
        setStripeConnected(false);
      } finally {
        setLoading(false);
      }
    };
    fetchStatus();
  }, []);

  // Don't render while loading or if Stripe is not connected
  if (loading || !stripeConnected) {
    return null;
  }

  return (
    <div className="bg-white/70 backdrop-blur-xl rounded-3xl border border-white/40 overflow-hidden">
      {/* Header */}
      <div className="px-6 py-4 bg-gradient-to-r from-purple-50/50 to-white border-b border-white/40">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-purple-100 rounded-xl">
            <CreditCard className="w-6 h-6 text-purple-600" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">Migration clients Stripe</h3>
            <p className="text-sm text-gray-500">
              Migrer vos clients Stripe existants vers la plateforme
            </p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-100">
        <TabButton
          active={activeTab === 'sync'}
          onClick={() => setActiveTab('sync')}
          icon={<RefreshCw className="w-4 h-4" />}
          label="Synchronisation API"
        />
        <TabButton
          active={activeTab === 'import'}
          onClick={() => setActiveTab('import')}
          icon={<Upload className="w-4 h-4" />}
          label="Import CSV"
        />
        <TabButton
          active={activeTab === 'manual'}
          onClick={() => setActiveTab('manual')}
          icon={<Link2 className="w-4 h-4" />}
          label="Association manuelle"
        />
      </div>

      {/* Content */}
      <div className="p-6">
        {activeTab === 'sync' && <SyncTab />}
        {activeTab === 'import' && <ImportTab />}
        {activeTab === 'manual' && <ManualTab />}
      </div>
    </div>
  );
}

// ==================== TAB BUTTON ====================

function TabButton({
  active,
  onClick,
  icon,
  label,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
}) {
  return (
    <button
      onClick={onClick}
      className={`
        flex-1 flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium
        transition-colors border-b-2
        ${
          active
            ? 'text-purple-600 border-purple-600 bg-purple-50/50'
            : 'text-gray-500 border-transparent hover:text-gray-700 hover:bg-gray-50'
        }
      `}
    >
      {icon}
      {label}
    </button>
  );
}

// ==================== SYNC TAB ====================

function SyncTab() {
  const [autoAssociate, setAutoAssociate] = useState(true);
  const [includePaymentMethods, setIncludePaymentMethods] = useState(true);
  const syncMutation = useSyncStripeCustomers();

  const handleSync = async () => {
    try {
      await syncMutation.mutateAsync({
        autoAssociate,
        includePaymentMethods,
      });
    } catch (error) {
      logger.error('Sync failed:', error);
    }
  };

  return (
    <div className="space-y-6">
      {/* Description */}
      <div className="p-4 rounded-xl bg-gray-50 border border-gray-100">
        <p className="text-sm text-gray-600">
          Cette option récupère automatiquement tous vos clients depuis votre compte Stripe et
          tente de les associer à vos clients existants en comparant les emails et noms.
        </p>
      </div>

      {/* Options */}
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <input
            type="checkbox"
            id="autoAssociateStripe"
            checked={autoAssociate}
            onChange={(e) => setAutoAssociate(e.target.checked)}
            className="w-5 h-5 text-purple-600 rounded border-gray-300 focus:ring-purple-500"
          />
          <label htmlFor="autoAssociateStripe" className="text-sm text-gray-700">
            Association automatique (score de correspondance &gt;= 80%)
          </label>
        </div>

        <div className="flex items-center gap-3">
          <input
            type="checkbox"
            id="includePaymentMethods"
            checked={includePaymentMethods}
            onChange={(e) => setIncludePaymentMethods(e.target.checked)}
            className="w-5 h-5 text-purple-600 rounded border-gray-300 focus:ring-purple-500"
          />
          <label htmlFor="includePaymentMethods" className="text-sm text-gray-700">
            Inclure les moyens de paiement associes
          </label>
        </div>
      </div>

      {/* Sync Button */}
      <button
        onClick={handleSync}
        disabled={syncMutation.isPending}
        className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-purple-600 text-white font-medium hover:bg-purple-700 transition-colors disabled:opacity-50"
      >
        {syncMutation.isPending ? (
          <>
            <Loader2 className="w-5 h-5 animate-spin" />
            Synchronisation en cours...
          </>
        ) : (
          <>
            <RefreshCw className="w-5 h-5" />
            Synchroniser avec Stripe
          </>
        )}
      </button>

      {/* Results */}
      {syncMutation.isSuccess && (
        <div className="p-4 rounded-xl bg-green-50 border border-green-100">
          <h4 className="font-medium text-green-800 mb-3">Synchronisation terminee</h4>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-center">
            <div>
              <p className="text-2xl font-bold text-green-600">
                {syncMutation.data.totalFetched}
              </p>
              <p className="text-xs text-green-600">Recuperes</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-blue-600">
                {syncMutation.data.newCustomers}
              </p>
              <p className="text-xs text-blue-600">Nouveaux</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-purple-600">
                {syncMutation.data.autoMatched}
              </p>
              <p className="text-xs text-purple-600">Auto-associes</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-yellow-600">
                {syncMutation.data.unmatched}
              </p>
              <p className="text-xs text-yellow-600">A associer</p>
            </div>
          </div>
        </div>
      )}

      {/* Error */}
      {syncMutation.isError && (
        <div className="flex items-center gap-2 p-4 rounded-xl bg-red-50 text-red-600">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <p className="text-sm">{syncMutation.error.message}</p>
        </div>
      )}
    </div>
  );
}

// ==================== IMPORT TAB ====================

function ImportTab() {
  const [csvData, setCsvData] = useState<StripeCSVImportRow[]>([]);
  const [parseError, setParseError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const importMutation = useImportStripeCustomers();

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setParseError(null);

    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target?.result as string;
      try {
        const rows = parseCSV(text);
        setCsvData(rows);
      } catch (error: any) {
        setParseError(error.message);
      }
    };
    reader.readAsText(file);
  };

  const parseCSV = (text: string): StripeCSVImportRow[] => {
    const lines = text.trim().split('\n');
    if (lines.length < 2) {
      throw new Error('Le fichier CSV doit contenir au moins un en-tete et une ligne de donnees');
    }

    const headers = lines[0].toLowerCase().split(',').map((h) => h.trim());
    const customerIdIndex = headers.findIndex(
      (h) => h.includes('customer') || h.includes('stripe')
    );
    const emailIndex = headers.findIndex((h) => h.includes('email'));
    const clientIdIndex = headers.findIndex((h) => h.includes('client') && h.includes('id'));

    if (customerIdIndex === -1) {
      throw new Error('Colonne stripeCustomerId non trouvee');
    }

    const rows: StripeCSVImportRow[] = [];
    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(',').map((v) => v.trim().replace(/^["']|["']$/g, ''));
      if (values[customerIdIndex]) {
        rows.push({
          stripeCustomerId: values[customerIdIndex],
          clientEmail: emailIndex !== -1 ? values[emailIndex] || undefined : undefined,
          clientId: clientIdIndex !== -1 ? values[clientIdIndex] || undefined : undefined,
        });
      }
    }

    if (rows.length === 0) {
      throw new Error('Aucune ligne valide trouvee dans le fichier');
    }

    return rows;
  };

  const handleImport = async () => {
    try {
      await importMutation.mutateAsync(csvData);
    } catch (error) {
      logger.error('Import failed:', error);
    }
  };

  const downloadTemplate = () => {
    const template = 'stripeCustomerId,clientEmail,clientId\ncus_XXXXX,client@example.com,';
    const blob = new Blob([template], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'stripe-migration-template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      {/* Description */}
      <div className="p-4 rounded-xl bg-gray-50 border border-gray-100">
        <p className="text-sm text-gray-600">
          Importez un fichier CSV contenant les IDs clients Stripe et les emails ou IDs
          clients correspondants. Format: stripeCustomerId, clientEmail ou clientId.
        </p>
      </div>

      {/* Template Download */}
      <button
        onClick={downloadTemplate}
        className="flex items-center gap-2 text-sm text-purple-600 hover:text-purple-700"
      >
        <Download className="w-4 h-4" />
        Telecharger le modele CSV
      </button>

      {/* File Upload */}
      <div>
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv"
          onChange={handleFileChange}
          className="hidden"
        />
        <button
          onClick={() => fileInputRef.current?.click()}
          className="w-full flex items-center justify-center gap-2 px-4 py-6 rounded-xl border-2 border-dashed border-gray-300 text-gray-500 hover:border-purple-400 hover:text-purple-600 transition-colors"
        >
          <FileText className="w-6 h-6" />
          <span>Cliquez pour selectionner un fichier CSV</span>
        </button>
      </div>

      {/* Parse Error */}
      {parseError && (
        <div className="flex items-center gap-2 p-3 rounded-xl bg-red-50 text-red-600">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <p className="text-sm">{parseError}</p>
        </div>
      )}

      {/* Preview */}
      {csvData.length > 0 && (
        <div className="p-4 rounded-xl bg-gray-50">
          <p className="text-sm font-medium text-gray-700 mb-2">
            {csvData.length} lignes prete(s) a importer
          </p>
          <div className="max-h-40 overflow-y-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500">
                  <th className="py-1">Customer ID</th>
                  <th className="py-1">Email</th>
                  <th className="py-1">Client ID</th>
                </tr>
              </thead>
              <tbody>
                {csvData.slice(0, 5).map((row, i) => (
                  <tr key={i} className="border-t border-gray-200">
                    <td className="py-1 font-mono text-xs">{row.stripeCustomerId}</td>
                    <td className="py-1">{row.clientEmail || '-'}</td>
                    <td className="py-1">{row.clientId || '-'}</td>
                  </tr>
                ))}
                {csvData.length > 5 && (
                  <tr className="border-t border-gray-200">
                    <td colSpan={3} className="py-1 text-gray-400 text-center">
                      ... et {csvData.length - 5} autres
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <button
            onClick={handleImport}
            disabled={importMutation.isPending}
            className="mt-4 w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-purple-600 text-white font-medium hover:bg-purple-700 transition-colors disabled:opacity-50"
          >
            {importMutation.isPending ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Import en cours...
              </>
            ) : (
              <>
                <Upload className="w-5 h-5" />
                Importer les clients
              </>
            )}
          </button>
        </div>
      )}

      {/* Results */}
      {importMutation.isSuccess && (
        <div className="p-4 rounded-xl bg-green-50 border border-green-100">
          <h4 className="font-medium text-green-800 mb-3">Import termine</h4>
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <p className="text-2xl font-bold text-gray-600">{importMutation.data.total}</p>
              <p className="text-xs text-gray-500">Total</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-green-600">{importMutation.data.success}</p>
              <p className="text-xs text-green-600">Succes</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-red-600">{importMutation.data.failed}</p>
              <p className="text-xs text-red-600">Echec</p>
            </div>
          </div>

          {importMutation.data.errors.length > 0 && (
            <div className="mt-4 p-3 rounded-lg bg-red-50 text-red-600 text-sm">
              <p className="font-medium mb-2">Erreurs:</p>
              <ul className="list-disc list-inside space-y-1">
                {importMutation.data.errors.slice(0, 3).map((err, i) => (
                  <li key={i}>
                    Ligne {err.row}: {err.error}
                  </li>
                ))}
                {importMutation.data.errors.length > 3 && (
                  <li>... et {importMutation.data.errors.length - 3} autres erreurs</li>
                )}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ==================== MANUAL TAB ====================

function ManualTab() {
  const { data: unassociated, isLoading, refetch } = useUnassociatedStripeCustomers();
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [searchCustomer, setSearchCustomer] = useState('');
  const associateMutation = useAssociateStripeCustomer();

  const handleAssociate = async (stripeCustomerId: string, clientId: string) => {
    try {
      await associateMutation.mutateAsync({ stripeCustomerId, clientId });
      setExpandedId(null);
    } catch (error) {
      logger.error('Association failed:', error);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Description */}
      <div className="p-4 rounded-xl bg-gray-50 border border-gray-100">
        <p className="text-sm text-gray-600">
          Associez manuellement les clients Stripe à vos clients. Les suggestions sont basées
          sur la correspondance d'email et de nom.
        </p>
      </div>

      {/* Refresh Button */}
      <div className="flex justify-end">
        <button
          onClick={() => refetch()}
          className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm text-gray-600 hover:bg-gray-100 transition-colors"
        >
          <RefreshCw className="w-4 h-4" />
          Actualiser
        </button>
      </div>

      {/* Search Customer */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
        <input
          type="text"
          value={searchCustomer}
          onChange={(e) => setSearchCustomer(e.target.value)}
          placeholder="Rechercher un ID ou email client..."
          className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-gray-200 focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500"
        />
      </div>

      {/* Unassociated Customers List */}
      {!unassociated || unassociated.length === 0 ? (
        <div className="flex items-center gap-3 p-6 rounded-xl bg-gray-50 text-center justify-center">
          <Check className="w-6 h-6 text-green-500" />
          <p className="text-gray-600">Tous les clients Stripe sont associes</p>
        </div>
      ) : (
        <div className="space-y-3">
          {unassociated
            .filter(
              (c) =>
                !searchCustomer ||
                c.stripeCustomerId.toLowerCase().includes(searchCustomer.toLowerCase()) ||
                c.email?.toLowerCase().includes(searchCustomer.toLowerCase())
            )
            .map((customer) => (
              <CustomerCard
                key={customer.id}
                customer={customer}
                expanded={expandedId === customer.id}
                onToggle={() => setExpandedId(expandedId === customer.id ? null : customer.id)}
                onAssociate={handleAssociate}
                isPending={associateMutation.isPending}
              />
            ))}
        </div>
      )}
    </div>
  );
}

// ==================== CUSTOMER CARD ====================

function CustomerCard({
  customer,
  expanded,
  onToggle,
  onAssociate,
  isPending,
}: {
  customer: UnassociatedStripeCustomer;
  expanded: boolean;
  onToggle: () => void;
  onAssociate: (customerId: string, clientId: string) => void;
  isPending: boolean;
}) {
  return (
    <div className="rounded-xl border border-gray-200 overflow-hidden">
      {/* Header */}
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors"
      >
        <div className="flex items-center gap-3">
          <Building2 className="w-5 h-5 text-gray-400" />
          <div className="text-left">
            <p className="font-mono text-sm text-gray-900">{customer.stripeCustomerId}</p>
            <p className="text-xs text-gray-500">
              {customer.name || customer.email || 'Client inconnu'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-500">
            {customer.suggestedClients.length} suggestion(s)
          </span>
          {customer.paymentMethods.length > 0 && (
            <span className="px-2 py-0.5 bg-purple-100 text-purple-600 rounded-full text-xs">
              {customer.paymentMethods.length} moyen(s) de paiement
            </span>
          )}
          {expanded ? (
            <ChevronUp className="w-5 h-5 text-gray-400" />
          ) : (
            <ChevronDown className="w-5 h-5 text-gray-400" />
          )}
        </div>
      </button>

      {/* Expanded Content */}
      {expanded && (
        <div className="p-4 space-y-4">
          {/* Customer Info */}
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <p className="text-gray-500">Email Stripe</p>
              <p className="font-medium">{customer.email || '-'}</p>
            </div>
            <div>
              <p className="text-gray-500">Nom</p>
              <p className="font-medium">{customer.name || '-'}</p>
            </div>
          </div>

          {/* Payment Methods */}
          {customer.paymentMethods.length > 0 && (
            <div>
              <p className="text-sm font-medium text-gray-700 mb-2">Moyens de paiement</p>
              <div className="flex flex-wrap gap-2">
                {customer.paymentMethods.map((pm) => (
                  <div
                    key={pm.id}
                    className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-gray-100 text-sm"
                  >
                    <CreditCard className="w-4 h-4 text-gray-500" />
                    <span className="capitalize">{pm.brand || pm.type}</span>
                    {pm.last4 && <span className="font-mono">****{pm.last4}</span>}
                    {pm.expMonth && pm.expYear && (
                      <span className="text-gray-400">
                        {String(pm.expMonth).padStart(2, '0')}/{pm.expYear}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Suggested Clients */}
          <div>
            <p className="text-sm font-medium text-gray-700 mb-2">Clients suggeres</p>
            {customer.suggestedClients.length > 0 ? (
              <div className="space-y-2">
                {customer.suggestedClients.map((suggestion) => (
                  <div
                    key={suggestion.clientId}
                    className="flex items-center justify-between p-3 rounded-lg bg-gray-50 hover:bg-purple-50 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className={`
                        w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold
                        ${
                          suggestion.matchScore >= 90
                            ? 'bg-green-100 text-green-600'
                            : suggestion.matchScore >= 70
                              ? 'bg-yellow-100 text-yellow-600'
                              : 'bg-gray-100 text-gray-600'
                        }
                      `}
                      >
                        {suggestion.matchScore}%
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{suggestion.clientName}</p>
                        <p className="text-xs text-gray-500">{suggestion.matchReason}</p>
                      </div>
                    </div>
                    <button
                      onClick={() => onAssociate(customer.stripeCustomerId, suggestion.clientId)}
                      disabled={isPending}
                      className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-purple-600 text-white text-sm font-medium hover:bg-purple-700 transition-colors disabled:opacity-50"
                    >
                      {isPending ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          <Link2 className="w-4 h-4" />
                          Associer
                        </>
                      )}
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500 italic">
                Aucune suggestion trouvee. Verifiez que le client existe dans votre base.
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default StripeMigrationPanel;
